import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Input, Select, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Eye, Save, Send, EyeOff } from 'lucide-react';
import { PageHeader, SectionCard, KeyValue, StatusBadge, QueryState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { errorText, formatDate } from '@/lib/format';
import { useCan } from '@rbac/useCan';
import { useCrudList, useCrudWrite } from '../hooks';
import ListingPreviewCard from '../components/ListingPreviewCard';
import VariantMatrix from '../components/VariantMatrix';
import { validateListing, listingPayload } from '../lib/listing';
import { paiseToRupees, rupeesToPaise } from '../lib/money';
import { modelName, modelSpecs, suggestTitle, labelOf, rowsOf } from '../lib/catalog';

const EMPTY = { unit_id: '', title: '', price: '', mrp: '', warranty_months: '6', status: 'draft' };

/**
 * SELL-04 — listing editor (/listings/:id, or /listings/new?unit=ID). Real
 * create/update. Price in rupees → paise. draft → published requires
 * catalog:publish and no failed safety check on the unit's inspection.
 */
export default function ListingEditor({ store }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const can = useCan();
  const { toast } = useToast();
  const write = useCrudWrite();
  const canEdit = can('catalog', isNew ? 'create' : 'update');
  const canPublish = can('catalog', 'publish');

  const existing = useCrudList('Listing', { where: { id: Number(id) || 0 }, with: ['InventoryUnit', 'InventoryUnit.ProductModel'], limit: 1 }, { enabled: !isNew });
  const listing = existing.data && existing.data[0];
  // For a new listing: in-stock units, minus those already listed.
  const units = useCrudList('InventoryUnit', { where: { stock_state: 'in_stock' }, with: ['ProductModel', 'Listing'], orderBy: { column: 'id', direction: 'desc' }, limit: 200 }, { enabled: isNew });
  const inspections = useQuery('merchantInspections', {}, { select: (r) => r.data });

  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (listing) {
      setF({
        unit_id: String(listing.unit_id), title: listing.title, price: paiseToRupees(listing.price_paise),
        mrp: paiseToRupees(listing.mrp_paise), warranty_months: String(listing.warranty_months ?? 0), status: listing.status,
      });
    }
  }, [listing]);
  useEffect(() => {
    if (isNew && params.get('unit')) setF((p) => ({ ...p, unit_id: params.get('unit') }));
  }, [isNew, params]);

  const freeUnits = useMemo(() => (units.data || []).filter((u) => !(u.Listing && u.Listing.length)), [units.data]);
  const unit = isNew ? (units.data || []).find((u) => String(u.id) === String(f.unit_id)) : listing && listing.InventoryUnit;
  const model = unit && unit.ProductModel;
  useEffect(() => {
    if (isNew && unit && !f.title) setF((p) => ({ ...p, title: suggestTitle(unit.ProductModel) }));
  }, [isNew, unit, f.title]);

  // A recorded inspection with failed safety checks blocks publishing.
  const inspection = unit && (inspections.data || []).find((r) => r.sku === unit.internal_sku && r.results);
  const blockers = inspection ? inspection.blockers || [] : [];

  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const field = (k) => ({ value: f[k], onChange: (e) => set({ [k]: e.target.value }), error: errors[k], disabled: !canEdit });

  const save = async (status) => {
    const next = { ...f, status };
    const e = validateListing(next, { canPublish, blockers });
    if (isNew && !f.unit_id) e.unit_id = 'Pick the unit this listing sells';
    setErrors(e);
    setFormError(e.status || '');
    if (Object.keys(e).length) return;
    try {
      const data = listingPayload(next);
      if (isNew) {
        const row = await write.create('Listing', { ...data, unit_id: Number(f.unit_id) });
        toast.success('Listing created', data.title);
        if (row && row.id) nav(`/listings/${row.id}`, { replace: true });
        else nav('/products?tab=listed');
      } else {
        await write.update('Listing', listing.id, data);
        toast.success(status === 'published' ? 'Sent to publish' : status === 'unpublished' ? 'Unpublished' : 'Saved', status === 'published' ? 'It goes live now, or after platform review for new stores and flagged listings.' : data.title);
        existing.reload();
      }
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };

  const body = (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-4">
        {isNew ? (
          <SectionCard title="Unit">
            <QueryState query={units}>
              {() => (
                <Select
                  label="In-stock unit"
                  required
                  searchable
                  closeOnSelect
                  placeholder={freeUnits.length ? 'Pick a unit…' : 'Every in-stock unit is already listed'}
                  options={freeUnits.map((u) => ({ value: String(u.id), label: `${u.internal_sku} · ${modelName(u.ProductModel)} · Grade ${u.grade}` }))}
                  value={f.unit_id}
                  onChange={(v) => set({ unit_id: v, title: '' })}
                  error={errors.unit_id}
                />
              )}
            </QueryState>
          </SectionCard>
        ) : null}
        {unit ? (
          <SectionCard title="Unit details">
            <KeyValue
              columns={2}
              items={[
                { label: 'SKU', value: unit.internal_sku },
                { label: 'Serial', value: <span className="font-mono">{unit.serial_masked || '—'}</span> },
                { label: 'Model', value: modelName(model) },
                { label: 'Specs', value: modelSpecs(model) },
                { label: 'Grade', value: unit.grade },
                { label: 'Condition', value: labelOf(unit.condition) },
                { label: 'Battery', value: unit.battery_health_pct != null ? `${unit.battery_health_pct}%` : '—' },
                { label: 'Stock', value: <StatusBadge status={unit.stock_state} /> },
              ]}
            />
          </SectionCard>
        ) : null}

        <SectionCard title="Offer">
          <Input label="Title" required maxLength={200} showCount {...field('title')} />
          <div className="grid gap-3 sm:grid-cols-3">
            <Input label="Selling price (₹)" required placeholder="28999" {...field('price')} helperText={rupeesToPaise(f.price) != null ? `${rupeesToPaise(f.price)} paise` : undefined} />
            <Input label="MRP (₹)" placeholder="38999" {...field('mrp')} />
            <Input label="Warranty (months)" type="number" {...field('warranty_months')} />
          </div>
          <p className="text-xs opacity-60">Price changes apply to new checkouts only; quotes already issued keep their price. A variant without its own price sells at this one.</p>
        </SectionCard>

        {/* MERCH-87: options and variants, once the listing exists (they hang off its id). */}
        {!isNew && listing ? <VariantMatrix listingId={listing.id} canEdit={canEdit} /> : null}

        {blockers.length ? (
          <Alert variant="warning" title="Publishing blocked by inspection">Safety checks not passed: {blockers.join(', ')}.</Alert>
        ) : unit && !inspection ? (
          <Alert variant="info">No inspection checklist is on record for this unit yet (preview data).</Alert>
        ) : null}
        {!canPublish ? <Alert variant="info">You can save drafts; publishing needs the catalog publish permission.</Alert> : null}
        {listing && listing.status === 'pending_review' ? (
          <Alert variant="info" title="Waiting for platform review">New stores' listings (first 30 days) and listings flagged by risk checks are reviewed before they go live. You'll see the decision here.</Alert>
        ) : null}
        {listing && listing.moderation_status === 'rejected' ? (
          <Alert variant="error" title="Not approved by the platform">{listing.moderation_note || 'No reason given.'}</Alert>
        ) : null}
        {formError ? <Alert variant="error">{formError}</Alert> : null}

        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" leftIcon={<Save size={16} />} onClick={() => save(isNew || f.status === 'draft' ? 'draft' : f.status)} loading={write.loading}>
              {isNew || f.status === 'draft' ? 'Save draft' : 'Save changes'}
            </Button>
            {f.status === 'pending_review' && !isNew ? (
              <Button leftIcon={<Send size={16} />} disabled>Awaiting review</Button>
            ) : f.status !== 'published' || isNew ? (
              <Button leftIcon={<Send size={16} />} onClick={() => save('published')} disabled={!canPublish || blockers.length > 0} loading={write.loading}>Publish</Button>
            ) : (
              <Button variant="destructive" leftIcon={<EyeOff size={16} />} onClick={() => save('unpublished')} disabled={!canPublish} loading={write.loading}>Unpublish</Button>
            )}
          </div>
        ) : null}
      </div>

      <aside className="space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><Eye size={16} /> Storefront preview</h2>
        <ListingPreviewCard
          title={f.title}
          pricePaise={rupeesToPaise(f.price)}
          mrpPaise={rupeesToPaise(f.mrp)}
          warrantyMonths={Number(f.warranty_months) || 0}
          unit={unit}
          model={model}
          storeName={store && store.name}
        />
      </aside>
    </div>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={isNew ? 'New listing' : (listing && listing.title) || 'Listing'}
        subtitle={!isNew && listing ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            <StatusBadge status={listing.status} />
            {listing.published_at ? `published ${formatDate(listing.published_at)}` : 'not yet published'}
          </span>
        ) : 'Sell one in-stock unit on your storefront.'}
        back={{ label: 'Products', href: '/products?tab=listed' }}
      />
      {isNew ? body : (
        <QueryState query={existing}>
          {(rows) => (rowsOf(rows).length ? body : <Alert variant="warning" title="Listing not found">It may have been removed.</Alert>)}
        </QueryState>
      )}
    </div>
  );
}
